<template>
  <v-dialog
    v-model="showDialog"
    :width="dialogWidth"
    eager
  >
    <template v-slot:activator="{ on }">
      <v-btn
        :class="buttonClass"
        :color="paymentMethod.color"
        block
        class="ml-0 text-subtitle-1"
        dark
        depressed
        large
        style="height: 55px"
        v-on="on"
        @click="selectPaymentMethod"
      >
        <v-icon
          class="mr-4"
          dark
          left
        >{{ icon }}</v-icon>
        {{ paymentMethod.name }}
      </v-btn>
    </template>

    <v-card
      v-if="paymentMethod.code === 'vs-giftcard'"
      class="grey lighten-5"
      tile
    >
      <v-toolbar
        color="grey lighten-3"
        flat
      >
        <v-toolbar-title>
          <v-icon
            class="mr-2 mt-n1"
            left
          >{{ icon }}</v-icon>
          {{ paymentMethod.name }}
        </v-toolbar-title>
      </v-toolbar>
      <v-card-text class="pt-3">
        <v-row>
          <v-col cols="5">
            <v-list
              nav
              style="background: #f9f9f9; border: 1px solid #ddd"
            >
              <v-subheader>{{ $t('Choose an {0}', [$t('Amount').toLowerCase()]) }}</v-subheader>
              <v-list-item-group
                v-for="amount in [10, 25, 50]"
                v-model="paidAmount"
                :key="amount"
                color="primary"
              >
                <v-list-item
                  :value="amount"
                  @click="focusPaymentMethodInput()"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ amount | money }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-col>
          <v-col cols="7">
            <numeric-keyboard
              :clear-input="showDialog"
              :keyboard-class="`keyboard-${paymentMethod.id}`"
              @clear="payload = {}; focusPaymentMethodInput()"
              @input="$set(payload, 'code', $event)"
            >
              <v-text-field
                v-comma
                slot="input"
                slot-scope="{ setKeyboardInput }"
                v-model="payload.code"
                :ref="`input`"
                :label="`${$t('Gift Card')} ${$t('Code').toLowerCase()}`"
                filled
                hide-details
                @input="setKeyboardInput($event)"
                @keypress.enter="addTransaction(paidAmount, payload)"
              />
            </numeric-keyboard>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="!paidAmount || _.isEmpty(payload) || _.get(payload, 'code') === ''"
          :loading="$wait.is('adding transaction')"
          class="font-weight-bold"
          color="primary"
          text
          @click="addTransaction(paidAmount, payload)"
        >
          {{ $t('Save') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card
      v-else
      class="grey lighten-5"
      tile
    >
      <v-toolbar
        color="grey lighten-3"
        flat
      >
        <v-toolbar-title>
          <v-icon
            class="mr-2 mt-n1"
            left
          >{{ icon }}</v-icon>
          {{ paymentMethod.name }}
        </v-toolbar-title>
      </v-toolbar>
      <v-card-text class="pt-3">
        <v-row>
          <v-col>
            <form-wrapper :validator="$v">
              <v-form
                ref="form"
                @submit.prevent
              >
                <form-group name="paidAmount">
                  <template v-slot:default="{attrs}">
                    <numeric-keyboard
                      :clear-input="showDialog"
                      :keyboard-class="`keyboard-${paymentMethod.id}`"
                      @clear="paidAmount = null; focusPaymentMethodInput()"
                      @input="paidAmount = $event"
                    >
                      <v-text-field
                        v-comma
                        slot="input"
                        slot-scope="{ setKeyboardInput }"
                        :ref="`input`"
                        v-model="paidAmount"
                        v-bind="attrs"
                        :label="$t('Amount')"
                        filled
                        hide-details
                        @input="setKeyboardInput($event)"
                        @keypress.enter="addTransaction(paidAmount, payload)"
                      />
                    </numeric-keyboard>
                  </template>
                </form-group>
                <v-slide-y-transition>
                  <p
                    v-show="errorMessage"
                    class="red--text mb-0 mt-4"
                  >{{ errorMessage }}</p>
                </v-slide-y-transition>
              </v-form>
            </form-wrapper>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="!paidAmount"
          :loading="$wait.is('adding transaction')"
          class="font-weight-bold"
          color="primary"
          text
          @click="addTransaction(paidAmount, payload)"
        >
          {{ $t('Save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { decimal, required } from 'vuelidate/lib/validators'
import { mapState } from 'vuex'
import NumericKeyboard from '@/components/NumericKeyboard'
import DrawerOpener from '@/components/DrawerOpener'
import validationMixin from '@/utils/mixins/validationMixin'

export default {
  name: 'SelectPaymentMethodDialog',

  components: {
    DrawerOpener,
    NumericKeyboard
  },

  mixins: [
    validationMixin
  ],

  props: {
    paymentMethodObject: {
      type: Object,
      required: true
    },
    buttonClass: {
      type: [String, Object],
      default: null
    }
  },

  validations () {
    return {
      paidAmount: {
        required,
        decimal
      }
    }
  },

  data: () => ({
    paidAmount: null,
    payload: {},
    paymentMethod: null,
    showDialog: false,
    errorMessage: ''
  }),

  computed: {
    ...mapState('RECEIPT', ['receipt']),
    dialogWidth () {
      switch (this.paymentMethod.code) {
        case 'vs-giftcard':
          return '500px'
        default:
          return '300px'
      }
    },
    icon () {
      switch (this.paymentMethod.code) {
        case 'cash':
          return 'fal fa-wallet'
        case 'electronic':
          return 'fal fa-credit-card'
        case 'transfer':
          return 'fal fa-money-check'
        case 'vs-giftcard':
        case 'giftcard':
          return 'fal fa-gift'
      }
    }
  },

  watch: {
    paymentMethodObject: {
      immediate: true,
      handler (paymentMethodObject) {
        this.paymentMethod = Object.assign({}, paymentMethodObject)
      }
    }
  },

  mounted () {
    // Put error messages in separate reactive data property
    this.$watch(
      () => this.$refs['input'].errorMessages,
      (messages) => {
        this.errorMessage = messages.length ? messages[0] : ''
      }
    )
  },

  methods: {
    addTransaction (paidAmount, payload = null) {
      if (!this.validate()) return
      if (!paidAmount) return
      if (this.$wait.is('adding transaction')) return

      this.$wait.start('adding transaction')
      const isCashPayment = this.paymentMethod.code === 'cash'

      return this.$store.dispatch('RECEIPT/ADD_TRANSACTION', {
        paymentMethodId: this.paymentMethod.id,
        amount: paidAmount,
        payload: payload
      })
        .then((data) => {
          this.helpers.toastSuccess(data.message)
          if (isCashPayment) {
            this.$emit('open-drawer', this.$t('Payment'))
          }

          this.$emit('done', this.paymentMethod)
        })
        .finally(() => {
          this.showDialog = false
          this.paidAmount = null
          this.resetValidation()
          this.$wait.end('adding transaction')
        })
    },

    focusPaymentMethodInput () {
      setTimeout(() => { this.$nextTick(this.$refs[`input`].focus) }, 300)
    },

    selectPaymentMethod () {
      this.focusPaymentMethodInput()
      if (this.paymentMethod.code === 'electronic') {
        this.paidAmount = this.receipt.outstandingBalance
      } else {
        this.paidAmount = null
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/dialogs/SelectPaymentMethodDialog.vue